import React, { useEffect, useMemo, useState } from 'react';
import { Facebook, Radio, Clock, ArrowRight, Play } from 'lucide-react';
import { IMAGES } from '../mock/mock';
import { getSermons } from '../lib/api';
import { useSettings } from '../hooks/useSettings';
import Reveal from '../components/Reveal';
import FacebookFeed from '../components/FacebookFeed';
import { Dialog, DialogContent } from '../components/ui/dialog';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const fbEmbed = (url) => `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=560&autoplay=true`;

const Sermons = () => {
  const settings = useSettings();
  const FACEBOOK_URL = settings.facebookUrl || 'https://www.facebook.com/LivingHopeKnox/';
  const [sermons, setSermons] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    getSermons().then((d) => { if (Array.isArray(d)) setSermons(d); }).catch(() => {});
  }, []);

  const hasSermons = sermons.length > 0;

  const activeMeta = useMemo(
    () => (active ? [active.series, active.scripture].filter(Boolean).join(' \u00b7 ') : ''),
    [active]
  );

  const thumb = (s) => {
    if ((s.videoType || 'youtube') === 'youtube' && s.youtubeId) {
      return `https://img.youtube.com/vi/${s.youtubeId}/hqdefault.jpg`;
    }
    return null;
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[52vh] min-h-[420px] flex items-center justify-center overflow-hidden">
        <img src={IMAGES.worshipStage} alt="Worship" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b2027]/80 via-[#0b2027]/55 to-[#0b2027]/85" />
        <div className="relative text-center px-6">
          <Reveal>
            <p className="text-[#c9a24b] uppercase tracking-[0.36em] text-xs mb-5">Messages</p>
            <h1 className="font-serif-display italic text-white text-5xl md:text-7xl">Sermons</h1>
          </Reveal>
        </div>
      </section>

      {/* WATCH LIVE */}
      <section className="bg-[#0f2e38] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#17565f]/40 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#2c8a95]/20 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 border border-[#c9a24b]/50 rounded-full px-5 py-2 mb-8">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#c9a24b] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#c9a24b]" />
              </span>
              <span className="text-[#c9a24b] uppercase tracking-[0.24em] text-xs">Live on Facebook</span>
            </div>
            <h2 className="font-serif-display text-white text-4xl md:text-6xl mb-6">Join Us Live</h2>
            <p className="text-white/75 text-lg leading-relaxed mb-4">
              Can{'\u2019'}t make it in person? Worship with us from anywhere. We stream our services live on Facebook every <span className="text-white font-600">Sunday at 10:30 AM</span>.
            </p>
            <p className="text-white/60 mb-10">
              The live stream will appear here when we go live. Follow our page so you never miss a message.
            </p>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="btn-shine inline-flex items-center gap-3 bg-[#c9a24b] text-[#0b2027] hover:bg-[#d8b566] px-10 py-4 uppercase tracking-[0.2em] text-sm font-700 transition-colors duration-300">
              <Facebook className="h-5 w-5" /> Watch Live
            </a>
          </Reveal>

          <Reveal delay={140} className="w-full">
            <FacebookFeed />
          </Reveal>
        </div>
      </section>

      {/* ARCHIVE */}
      <section className="bg-[#f6f2ea] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {hasSermons ? (
            <>
              <Reveal className="text-center mb-14">
                <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-4">Message Archive</p>
                <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-5xl">Recent Sermons</h2>
              </Reveal>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {sermons.map((s, i) => {
                  const t = thumb(s);
                  return (
                    <Reveal key={s.id} delay={(i % 3) * 100}>
                      <button onClick={() => setActive(s)} className="group text-left w-full">
                        <div className="relative overflow-hidden rounded-md">
                          {t ? (
                            <img src={t} alt={s.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700" />
                          ) : (
                            <div className="w-full h-56 bg-[#0f2e38] flex items-center justify-center">
                              <Facebook className="h-12 w-12 text-[#1877F2]" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-[#0b2027]/20 group-hover:bg-[#0b2027]/40 transition-colors" />
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="h-14 w-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <Play className="h-6 w-6 text-[#0f2e38] ml-0.5" fill="#0f2e38" />
                            </span>
                          </span>
                        </div>
                        <div className="pt-5">
                          {s.series && <span className="text-xs uppercase tracking-[0.2em] text-[#2c8a95]">{s.series}</span>}
                          <h3 className="font-serif-display text-2xl text-[#0f2e38] mt-2 group-hover:text-[#17565f] transition-colors">{s.title}</h3>
                          <p className="text-[#5a6b71] text-sm mt-2">{s.speaker}{s.date ? ` \u00b7 ${formatDate(s.date)}` : ''}</p>
                        </div>
                      </button>
                    </Reveal>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="max-w-4xl mx-auto">
              <Reveal className="text-center">
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-full border border-[#17565f]/40 mb-8">
                  <Radio className="h-9 w-9 text-[#17565f]" />
                </div>
                <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-4">Message Archive</p>
                <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-5xl mb-6">Sermons Coming Soon</h2>
                <p className="text-[#3a4d54] text-lg leading-relaxed max-w-2xl mx-auto mb-12">
                  We{'\u2019'}re building a library of past messages so you can revisit teaching any time. Our on-demand sermon archive is on the way. In the meantime, catch every message live or replay recent services on our Facebook page.
                </p>
              </Reveal>
              <Reveal delay={120}>
                <div className="grid sm:grid-cols-3 gap-6">
                  {['a', 'b', 'c'].map((k) => (
                    <div key={`placeholder-${k}`} className="bg-white border border-[#e4ddd0] p-6 text-center">
                      <div className="relative mb-5 overflow-hidden rounded-md bg-[#e9e2d5] h-36 flex items-center justify-center">
                        <Clock className="h-8 w-8 text-[#c1b9a8]" />
                      </div>
                      <div className="h-3 w-2/3 mx-auto bg-[#e9e2d5] rounded mb-3" />
                      <div className="h-2.5 w-1/2 mx-auto bg-[#efe9dd] rounded" />
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={200} className="text-center mt-14">
                <p className="text-[#5a6b71] mb-6">Want to be notified when sermons go live here?</p>
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 text-[#17565f] uppercase tracking-[0.2em] text-sm font-600">
                  Follow Us on Facebook
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {/* PLAYER MODAL */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl w-[92vw] p-0 bg-[#0b2027] border-[#17565f] overflow-hidden">
          {active && (
            <div>
              <div className="relative pb-[56.25%] h-0 bg-black">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={(active.videoType || 'youtube') === 'facebook'
                    ? fbEmbed(active.videoUrl)
                    : `https://www.youtube.com/embed/${active.youtubeId}?autoplay=1&rel=0`}
                  title={active.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-6">
                {(active.series || active.scripture) && (
                  <p className="text-[#c9a24b] uppercase tracking-[0.24em] text-xs mb-2">
                    {activeMeta}
                  </p>
                )}
                <h3 className="font-serif-display text-white text-2xl">{active.title}</h3>
                <p className="text-white/60 text-sm mt-1">{active.speaker}{active.date ? ` \u00b7 ${formatDate(active.date)}` : ''}</p>
                {active.description && <p className="text-white/70 mt-3 leading-relaxed">{active.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sermons;
